//Librerias
import React, { useState, useEffect, useMemo, useRef } from "react";
import { VictoryPie } from "victory";

//Iconos
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/free-solid-svg-icons";

//API
import { getEstadisticas, getEstadisticasPorTipo } from "../../api/reservas";

//Estilos
import "./Dashboard.css";
import "./DashboardsReservasCanchas.css";

// Dashboard "Estado": cada estado del enum `estado` de canchaReservas.
// El orden define columnas, colores del pie y etiquetas.
const ESTADOS_CONFIG = [
    { key: "confirmadas", label: "Confirmadas", color: "#06b698", labelClass: "turquoiseColor" },
    { key: "canceladas", label: "Canceladas", color: "red", labelClass: "redColor" },
    { key: "suspendidas", label: "Suspendidas", color: "grey", labelClass: "greyColor" },
];

// Dashboard "Reservas por Tipo": los 6 tipos de juego creables (claves reales del enum `tipoJuego`).
// SINGLES = 60min, DOBLES = 75min (claves del enum, no "SINGLES_60" / "DOBLES_75").
const TIPOS_CONFIG = [
    { key: "singles", label: "Singles 60′", color: "#06b698", labelClass: "turquoiseColor" },
    { key: "singles45", label: "Singles 45′", color: "green", labelClass: "greenColor" },
    { key: "dobles", label: "Dobles 75′", color: "orange", labelClass: "orangeColor" },
    { key: "dobles60", label: "Dobles 60′", color: "lightblue", labelClass: "lightblueColor" },
    { key: "singlesPadel", label: "Singles Pádel", color: "rgb(240, 61, 240)", labelClass: "violetColor" },
    { key: "doblesPadel", label: "Dobles Pádel", color: "rgb(233, 233, 4)", labelClass: "yellowColor" },
];

// Helper: Date -> "YYYY-MM-DD"
const toInputDate = (date) => date.toISOString().substring(0, 10);

const DashboardsReservasCanchas = ({ setGridToShow }) => {

    // Rango por defecto: ultimos 30 dias
    const defaults = useMemo(() => {
        const hasta = new Date();
        const desde = new Date();
        desde.setDate(desde.getDate() - 30);
        return { desde: toInputDate(desde), hasta: toInputDate(hasta) };
    }, []);

    const [fechaDesde, setFechaDesde] = useState(defaults.desde);
    const [fechaHasta, setFechaHasta] = useState(defaults.hasta);
    const [infoEstado, setInfoEstado] = useState(null);
    const [infoTipo, setInfoTipo] = useState(null);
    const [loading, setLoading] = useState(true);

    const firstLoadRef = useRef(true);

    const rangoValido = !!fechaDesde && !!fechaHasta && fechaDesde <= fechaHasta;

    // Ambos dashboards comparten el mismo total (reservas del rango)
    const totalReservas = infoTipo?.total ?? infoEstado?.total ?? 0;
    const hayDatos = !!infoEstado || !!infoTipo;

    // Un solo fetch alimenta ambos dashboards (filtros compartidos)
    const buscarEstadisticas = async (desde, hasta) => {
        setLoading(true);
        try {
            const [respEstado, respTipo] = await Promise.all([
                getEstadisticas(desde, hasta),
                getEstadisticasPorTipo(desde, hasta),
            ]);
            // resp.data === { status, data: {...} }
            setInfoEstado(respEstado?.data?.data ?? null);
            setInfoTipo(respTipo?.data?.data ?? null);
        } catch (error) {
            console.log("Error en DashboardsReservasCanchas buscarEstadisticas");
            console.log(error);
            setInfoEstado(null);
            setInfoTipo(null);
        }
        setLoading(false);
    };

    // Carga inicial con el rango por defecto
    useEffect(() => {
        if (firstLoadRef.current) {
            firstLoadRef.current = false;
            buscarEstadisticas(defaults.desde, defaults.hasta);
        }
    }, [defaults]);

    const handleBuscar = () => {
        if (!rangoValido) return;
        buscarEstadisticas(fechaDesde, fechaHasta);
    };

    // Render reutilizable para cada dashboard de tipo "pie + conteos".
    // numbersLayout: "column" (default) | "grid" (filas de a dos) | "row" (una sola fila).
    const renderPieDashboard = (title, config, data, { numbersLayout = "column" } = {}) => {
        const numbersClass = numbersLayout === "grid" ? "numbersGrid" : numbersLayout === "row" ? "numbersRow" : "columnDirection";
        const total = data?.total ?? 0;
        const pct = (val) => (total > 0 ? ((val / total) * 100).toFixed(2) : "0.00");
        const pieData = config
            .map((c) => ({ x: c.label, y: data?.[c.key] ?? 0, color: c.color }))
            .filter((d) => d.y > 0);
        const colorScale = pieData.map((d) => d.color);

        return <div className="dashboard reservasDashboardFull">
            <h3>{title}</h3>
            <div className="infoContainer">

                <div className={`numbersDiv ${numbersClass}`}>
                    {config.map((c) => (
                        <div className="numbersData" key={c.key}>
                            <h4>{data?.[c.key] ?? 0}</h4>
                            <label className={c.labelClass}>{c.label} · {pct(data?.[c.key] ?? 0)}%</label>
                        </div>
                    ))}
                </div>

                <div className="percentageDiv">
                    {/* Sin labels dentro del pie: con porciones chicas y adyacentes se solapan.
                        El porcentaje vive en la columna de conteos, que actua de leyenda. */}
                    <VictoryPie
                        data={pieData}
                        colorScale={colorScale}
                        radius={100}
                        innerRadius={50}
                        labels={() => ""}
                    />
                </div>

            </div>
        </div>;
    };

    return <div className="dashboardParentContainer">
        <div className="utilitiesDiv reservasDashboardFilters">
            <button onClick={() => setGridToShow('')} className="goBackBtn"><FontAwesomeIcon className="iconArrowBack" icon={faArrowLeftLong} /></button>
            <div className="rangoFechasDiv">
                <label>
                    Desde
                    <input
                        type="date"
                        value={fechaDesde}
                        max={fechaHasta || undefined}
                        onChange={(e) => setFechaDesde(e.target.value)}
                    />
                </label>
                <label>
                    Hasta
                    <input
                        type="date"
                        value={fechaHasta}
                        min={fechaDesde || undefined}
                        onChange={(e) => setFechaHasta(e.target.value)}
                    />
                </label>
                <button
                    className={!rangoValido || loading ? "disabled" : ""}
                    disabled={!rangoValido || loading}
                    onClick={handleBuscar}
                >Buscar</button>
            </div>
            {!loading && hayDatos && <div className="totalReservasBox">
                <span className="totalReservasNumber">{totalReservas}</span>
                <span className="totalReservasLabel">Total Reservas</span>
            </div>}
        </div>

        {
            loading ? <h3 className="warningStatusMsg">Estamos modelando sus datos<span>.</span><span>.</span><span>.</span></h3>
            : (!infoEstado && !infoTipo) ? <h3 className="warningStatusMsg">No hay datos para el rango seleccionado</h3>
            : <div className="reservasDashboardsStack">
                {renderPieDashboard("Reservas por Tipo", TIPOS_CONFIG, infoTipo, { numbersLayout: "grid" })}
                {renderPieDashboard("Estado", ESTADOS_CONFIG, infoEstado, { numbersLayout: "row" })}
            </div>
        }
    </div>;
};

export default DashboardsReservasCanchas;
